/**
 * Superkraftmats eget samtykkebanner (eieren valgte variant C 2026-10-05). Banneret og
 * preferansevinduet er temaets, samtykket er Shopifys: alle valg går gjennom
 * `Shopify.customerPrivacy.setTrackingConsent`, som setter samtykket, sender
 * `visitorConsentCollected` og dermed driver samtykkeporten (`skm-samtykke.js`), Shopifys
 * piksler og Consent Mode-broen til Stape akkurat som Shopifys eget banner gjorde.
 *
 * Kontrakten (shopify.dev/docs/api/customer-privacy, lest 2026-10-05):
 * - `shouldShowBanner()` sier bare at besøkende er i en region som skal ha banner. Den sier ikke
 *   om valget alt er tatt («doesn't indicate if consent has already been given»), og målt i
 *   butikken er den sann også etter et valg (docs/cookie-grunnlag/maaling-2026-09-13/tilstand/03).
 *   Banneret vises derfor bare når den er sann og `currentVisitorConsent()` ikke har et lagret
 *   valg (se `venterPaaValg`). Ellers kom banneret igjen på hver side etter Godta eller Avvis.
 * - `setTrackingConsent({ analytics, marketing, preferences }, callback)` lagrer valget.
 *   `sale_of_data` røres ikke; det styres av opt-out-sida og GPC.
 * - `currentVisitorConsent()` svarer 'yes', 'no' eller '' per formål. Brukes til å avgjøre om
 *   valget er tatt, og til å fylle preferansevinduet.
 *
 * Trer til side for Shopifys banner. Så lenge Shopifys eget banner er slått på i adminen, viser
 * Shopify `#shopify-pc__banner` til den samme besøkende som `shouldShowBanner()` gjelder. Står
 * det elementet når siden er lastet (pluss SHOPIFY_VENTETID_MS), vises ikke vårt. Dukker det opp
 * senere, viker vårt, så det aldri står to. Temaet kan derfor publiseres før eieren slår av
 * Shopifys banner, og rulles tilbake ved å slå det på igjen. Footerknappen foretrekker Shopifys
 * dialog når `window.privacyBanner` finnes (se `koblInnstillingsknapp`), ellers vår.
 *
 * Fail-closed: laster ikke API-et, vises ingenting og ingenting lagres. I regioner som krever
 * samtykke er da alt valgfritt sperret, som før.
 *
 * Et valg regnes som lagret først når `setTrackingConsent` har svart uten feil. Mangler API-et,
 * kaster kallet eller melder tilbakekallet feil, blir banneret og vinduet stående med en kort
 * melding, og valget kan gjøres på nytt. Trekkes statistikk eller markedsføring tilbake, slettes
 * cookiene fra de formålene som JavaScript kan slette (`slettCookies`). HttpOnly-cookier som
 * FPID og _gtmeec kan bare fjernes av serveren.
 */

import { naarIkkeTillatt } from '@theme/skm-samtykke';

const API_VENTETID_MS = 5000;
/** Hvor lenge vi venter på tegn fra Shopifys banner etter at siden er lastet. */
const SHOPIFY_VENTETID_MS = 1500;
const SHOPIFY_BANNER_ID = 'shopify-pc__banner';

/** @typedef {{ analytics: boolean, marketing: boolean, preferences: boolean }} Valg */

const FEILMELDING = 'Valget ble ikke lagret. Prøv igjen om litt.';

/**
 * Cookies per formål som nettleseren kan slette. Navn med `*` er prefiks (`_ga_<id>`). Nødvendige
 * Shopify-cookies (kurv, økt, samtykke, localization, cart_currency) står aldri her.
 */
const COOKIES_PER_FORMAAL = {
  analytics: ['_ga', '_ga_*', '_gid', 'FPID', 'FPLC'],
  marketing: ['_fbp', '_gtmeec', '_dcid', '__kla_id', 'FPAU', 'FPGSID'],
};

/**
 * Sletter cookiene til de formålene som er avslått, med path / på både vertsdomenet og
 * .superkraftmat.no. HttpOnly-cookier er usynlige og urørlige for JavaScript og blir stående.
 * @param {Valg} valg
 * @param {Document} dok
 * @param {string} vert
 */
export function slettCookies(valg, dok = document, vert = location.hostname) {
  const navn = [];
  for (const formaal of /** @type {const} */ (['analytics', 'marketing'])) {
    if (!valg[formaal]) navn.push(...COOKIES_PER_FORMAAL[formaal]);
  }
  if (!navn.length) return;
  const finnes = dok.cookie.split(';').map((c) => /** @type {string} */ (c.split('=')[0]).trim()).filter(Boolean);
  const maal = new Set();
  for (const n of navn) {
    if (n.endsWith('*')) finnes.filter((f) => f.startsWith(n.slice(0, -1))).forEach((f) => maal.add(f));
    else maal.add(n);
  }
  const domener = ['', `; domain=${vert}`];
  if (vert === 'superkraftmat.no' || vert.endsWith('.superkraftmat.no')) domener.push('; domain=.superkraftmat.no');
  for (const n of maal) {
    for (const domene of domener) {
      dok.cookie = `${n}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; max-age=0; path=/${domene}`;
    }
  }
}

/**
 * Om Shopifys eget banner står i dokumentet. Bare elementet teller: skriptet og
 * `window.privacyBanner` kan finnes uten at Shopify viser noe banner, og da ville vi stått
 * uten banner i det hele tatt.
 * @param {Document} dok
 * @returns {boolean}
 */
export function shopifyBannerVist(dok = document) {
  return !!dok.getElementById(SHOPIFY_BANNER_ID);
}

/**
 * Leser preferansevinduets startverdier fra Shopify. Bare et aktivt ja gir avkrysset boks.
 * @param {any} api
 * @returns {Valg}
 */
export function lesValg(api) {
  /** @type {Record<string, string>} */
  let naa = {};
  try {
    naa = api && typeof api.currentVisitorConsent === 'function' ? api.currentVisitorConsent() || {} : {};
  } catch (error) {
    naa = {};
  }
  return {
    analytics: naa.analytics === 'yes',
    marketing: naa.marketing === 'yes',
    preferences: naa.preferences === 'yes',
  };
}

/**
 * Om besøkende ennå ikke har valgt: et av de tre formålene står tomt i `currentVisitorConsent()`.
 * Godta alle, Avvis alle og Lagre valgene setter alle tre til 'yes' eller 'no'. Samme regel som
 * velkomstbrevet bruker (`skm-velkomst.js`). Mangler eller feiler oppslaget, vet vi ikke at noe
 * er valgt, og da avgjør `shouldShowBanner()` alene som før.
 * @param {any} api
 * @returns {boolean}
 */
export function venterPaaValg(api) {
  try {
    if (!api || typeof api.currentVisitorConsent !== 'function') return true;
    const naa = api.currentVisitorConsent();
    if (!naa) return true;
    return ['analytics', 'marketing', 'preferences'].some((formaal) => !naa[formaal]);
  } catch (error) {
    return true;
  }
}

/** @returns {Promise<any>} API-et, eller `null` ved feil eller tidsgrense. */
function lastApi() {
  return new Promise((resolve) => {
    const shopify = /** @type {any} */ (window).Shopify;
    /** @param {any} api */
    const klar = (api) => !!api && typeof api.setTrackingConsent === 'function' && typeof api.shouldShowBanner === 'function';
    if (shopify && klar(shopify.customerPrivacy)) {
      resolve(shopify.customerPrivacy);
      return;
    }
    if (!shopify || typeof shopify.loadFeatures !== 'function') {
      resolve(null);
      return;
    }
    const klokke = setTimeout(() => resolve(null), API_VENTETID_MS);
    try {
      shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (/** @type {any} */ error) => {
        clearTimeout(klokke);
        const api = /** @type {any} */ (window).Shopify?.customerPrivacy;
        resolve(error || !klar(api) ? null : api);
      });
    } catch (error) {
      clearTimeout(klokke);
      resolve(null);
    }
  });
}

/** Venter til siden er lastet, og så litt til, så Shopifys banner rekker å melde seg. */
function ventPaaShopify() {
  return new Promise((resolve) => {
    const etter = () => setTimeout(resolve, SHOPIFY_VENTETID_MS);
    if (document.readyState === 'complete') etter();
    else window.addEventListener('load', etter, { once: true });
  });
}

function start() {
  const banner = /** @type {HTMLElement | null} */ (document.getElementById('SkmSamtykke'));
  const vindu = /** @type {HTMLDialogElement | null} */ (document.getElementById('SkmSamtykkeValg'));
  if (!banner || !vindu) return;

  const apiLoefte = lastApi();
  const lagringsknapper = /** @type {NodeListOf<HTMLButtonElement>} */ (document.querySelectorAll(
    '[data-skm-samtykke="godta"], [data-skm-samtykke="avslaa"], #SkmSamtykkeValg button[type="submit"]',
  ));
  let lagrer = false;

  /**
   * @param {Valg} valg
   * @returns {Promise<boolean>} Om valget faktisk ble lagret.
   */
  const lagre = async (valg) => {
    const api = await apiLoefte;
    if (!api) return false;
    return new Promise((resolve) => {
      try {
        api.setTrackingConsent(
          { analytics: valg.analytics, marketing: valg.marketing, preferences: valg.preferences },
          (/** @type {any} */ error) => resolve(!error),
        );
      } catch (error) {
        resolve(false);
      }
    });
  };

  /** @param {boolean} vis */
  const visFeil = (vis) => {
    for (const melding of document.querySelectorAll('[data-skm-samtykke-feil]')) {
      melding.textContent = vis ? FEILMELDING : '';
      /** @type {HTMLElement} */ (melding).hidden = !vis;
    }
  };

  const skjulBanner = () => {
    banner.hidden = true;
  };

  const visInnstillinger = async () => {
    const api = await apiLoefte;
    if (!api) return;
    const valg = lesValg(api);
    for (const formaal of /** @type {const} */ (['analytics', 'marketing', 'preferences'])) {
      const boks = /** @type {HTMLInputElement | null} */ (vindu.querySelector(`input[name="${formaal}"]`));
      if (boks) boks.checked = valg[formaal];
    }
    if (!vindu.open) vindu.showModal();
  };

  /** @param {Valg} valg */
  const velg = async (valg) => {
    if (lagrer) return;
    lagrer = true;
    for (const knapp of lagringsknapper) knapp.disabled = true;
    try {
      visFeil(false);
      if (!(await lagre(valg))) {
        visFeil(true);
        return;
      }
      skjulBanner();
      if (vindu.open) vindu.close();
    } finally {
      lagrer = false;
      for (const knapp of lagringsknapper) knapp.disabled = false;
    }
  };
  const alle = { analytics: true, marketing: true, preferences: true };
  const ingen = { analytics: false, marketing: false, preferences: false };

  for (const knapp of document.querySelectorAll('[data-skm-samtykke]')) {
    knapp.addEventListener('click', () => {
      const handling = knapp.getAttribute('data-skm-samtykke');
      if (handling === 'godta') velg(alle);
      else if (handling === 'avslaa') velg(ingen);
      else if (handling === 'velg') visInnstillinger();
      else if (handling === 'lukk') vindu.close();
    });
  }

  vindu.querySelector('form')?.addEventListener('submit', (event) => {
    event.preventDefault();
    const skjema = /** @type {HTMLFormElement} */ (event.currentTarget);
    const er = (/** @type {string} */ navn) => /** @type {HTMLInputElement | null} */ (skjema.elements.namedItem(navn))?.checked === true;
    velg({ analytics: er('analytics'), marketing: er('marketing'), preferences: er('preferences') });
  });

  // Footerknappen «Cookie-innstillinger» finner denne gjennom koblInnstillingsknapp, og
  // foretrekker Shopifys dialog når den finnes. Registreres først når API-et har lastet, så
  // knappen bare vises når vinduet faktisk kan lagre; laster det ikke, forblir knappen skjult.
  apiLoefte.then((api) => {
    if (!api) return;
    /** @type {any} */ (window).skmSamtykkebanner = { visInnstillinger };
    document.dispatchEvent(new CustomEvent('skm-samtykkebanner-klar'));
  });

  Promise.all([apiLoefte, ventPaaShopify()]).then(([api]) => {
    if (!api) return;
    let skalVises = false;
    try {
      skalVises = api.shouldShowBanner() === true && venterPaaValg(api);
    } catch (error) {
      skalVises = false;
    }
    if (!skalVises || shopifyBannerVist()) return;

    banner.hidden = false;

    // Dukker Shopifys banner opp likevel (treg side), viker vårt, så det aldri står to.
    const vakt = new MutationObserver(() => {
      if (shopifyBannerVist()) {
        skjulBanner();
        vakt.disconnect();
      }
    });
    vakt.observe(document.body, { childList: true });
  });
}

if (typeof document !== 'undefined') {
  naarIkkeTillatt('analytics', () => slettCookies({ analytics: false, marketing: true, preferences: true }));
  naarIkkeTillatt('marketing', () => slettCookies({ analytics: true, marketing: false, preferences: true }));
  if (document.getElementById('SkmSamtykke')) start();
}
